import React, { ReactElement, useRef, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import {
    Alert,
    AppBar,
    ArrowDropDownIcon,
    ArrowDropUpIcon,
    Box,
    Button,
    DownloadIcon,
    Grid,
    HelpOutlineIcon,
    IconButton,
    InfoIcon,
    Logo,
    Menu,
    MenuIcon,
    MenuItem,
    Toolbar,
    Tooltip,
    Typography
} from '@methanesat/ui-components';

import { IntroModal } from '../IntroModal';
import { useTranslate } from '../../hooks';
import {
    setGlobalDate,
    setEmissionsMapViewState,
    setPlatform,
    selectPlatform,
    resetSelectedFeatureParams,
    selectMethaneProduct,
    setAreaEmissionsProduct
} from '../../reducers';
import { AreaEmissionsProducts, PageIds, Platforms } from '../../types';
import Link from '../../components/Link';
import { INTERNAL_ONLY_SIGNAGE } from '../../environmentVariables';
import { getDefaultGlobalDate, initialViewStates } from '../../consts';
import { analytics, getFromLocalStorage, setInLocalStorage } from '../../utils';
import { doesUserAllowNonessentialCookies } from '../../utils/cookie';
import type { RootState } from '../../store';
/**
 * Universal page header.
 * @todo Add global CSS, componentDidCatch for error handling, persisting some layout
 * between pages (header?).
 */
interface HeaderProps {
    onOpenLegend?: () => void;
    isLegendOpen?: boolean;
    helpButtonRef?: React.RefObject<HTMLButtonElement | null>;
    page?: PageIds;
}

const Header = ({ onOpenLegend, isLegendOpen, helpButtonRef, page }: HeaderProps): ReactElement => {
    const t = useTranslate();
    const dispatch = useDispatch();
    const ogiNearbyEnabled = useSelector((state: RootState) => state.pages.emissions.layers.ogiLayers.ogiNearbyEnabled);

    // TODO: DP-731 Remove useMediaQuery once issue with
    // multiple SVGs is resolved (DP-726)
    const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);

    const anchorElement = useRef<null | HTMLElement>(null);

    const open = Boolean(anchorEl);

    const selectedPlatform = useSelector(selectPlatform);
    const selectedProduct = useSelector(selectMethaneProduct);

    const handleClick = () => {
        setAnchorEl(anchorElement.current);
    };

    const handlePlatformSelection = (platform: Platforms) => {
        dispatch(resetSelectedFeatureParams());
        dispatch(setPlatform(platform));

        if (selectedProduct !== AreaEmissionsProducts.l4) {
            dispatch(setAreaEmissionsProduct(AreaEmissionsProducts.l4));
        }

        dispatch(setEmissionsMapViewState(initialViewStates[platform]));
        dispatch(setGlobalDate(getDefaultGlobalDate(platform).value));
        handleClose();
        analytics.selectPlatform({ platform: platform });
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    // Intro modal handling
    const [introModalOpen, setIntroModalOpen] = React.useState(false);
    const handleIntroModalOpen = () => setIntroModalOpen(true);

    const enableForComparisonView = page === PageIds.comparisonPage;
    const isMainPage = page === PageIds.emissionsMap;

    useEffect(() => {
        let introModalDismissed = getFromLocalStorage('introModalDismissed');
        if (!introModalDismissed) {
            /**
             * If the user denies nonessential cookies, then they've visited before and we should
             * treat the intro modal as dismissed.  See https://methanesat.atlassian.net/browse/DP-4688
             */
            const userAllowsNonessentialCookies = doesUserAllowNonessentialCookies();
            if (
                enableForComparisonView ||
                (!userAllowsNonessentialCookies && typeof userAllowsNonessentialCookies === 'boolean')
            ) {
                introModalDismissed = true;
            }
        }
        setIntroModalOpen(!introModalDismissed);
    }, [enableForComparisonView]);

    // Closes the intro modal and, if specified, opens the legend
    const handleIntroModalAction = (action: 'close' | 'closeAndOpenLegend') => {
        setIntroModalOpen(false);
        if (action === 'closeAndOpenLegend') {
            onOpenLegend?.();
        }
        setInLocalStorage('introModalDismissed', true);
    };

    return (
        <>
            <AppBar position="static" color="inherit" sx={{ zIndex: (theme) => theme.zIndex.drawer + 1 }}>
                <Toolbar>
                    <Grid
                        container
                        sx={{
                            width: '100%',
                            justifyContent: 'space-between',
                            alignItems: 'center'
                        }}
                    >
                        <Grid size="auto">
                            <Link data-testid="logo" href="https://www.methanesat.org/" tabIndex={0} target="_blank">
                                <Logo ariaLabel={t('header.ariaLogoLabel')} />
                            </Link>
                        </Grid>
                        {INTERNAL_ONLY_SIGNAGE ? (
                            <Grid size="auto">
                                <Alert
                                    sx={{
                                        paddingTop: 0,
                                        paddingBottom: 0
                                    }}
                                    variant="filled"
                                    severity="warning"
                                >
                                    <Grid container display="flex">
                                        <Typography
                                            variant="caption"
                                            sx={{
                                                display: { xs: 'none', sm: 'none', md: 'flex' },
                                                paddingLeft: 1,
                                                paddingRight: 1
                                            }}
                                        >
                                            INTERNAL USE ONLY: DO NOT DISTRIBUTE
                                        </Typography>
                                        <Typography
                                            variant="caption"
                                            sx={{
                                                display: { xs: 'flex', sm: 'flex', md: 'none' }
                                            }}
                                        >
                                            INTERNAL USE ONLY
                                        </Typography>
                                    </Grid>
                                </Alert>
                            </Grid>
                        ) : null}
                        <Grid size="auto">
                            <Grid container alignItems="center">
                                <Grid size="auto">
                                    <Box component="div" margin={0} ref={anchorElement}>
                                        {/* desktop instrument menu */}
                                        {isMainPage && (
                                            <Button
                                                data-testid="instrument-menu-selector"
                                                id="instrument-menu-selector"
                                                aria-label={t('header.ariaMenuLabel')}
                                                onClick={handleClick}
                                                color="inherit"
                                                sx={{
                                                    display: {
                                                        xs: 'none',
                                                        sm: 'flex',
                                                        md: 'flex'
                                                    },
                                                    textTransform: 'none'
                                                }}
                                            >
                                                {selectedPlatform === Platforms.MAIR
                                                    ? t(`platforms.MethaneAIR.long`)
                                                    : t(`platforms.MethaneSAT.long`)}
                                                {open ? (
                                                    <ArrowDropUpIcon></ArrowDropUpIcon>
                                                ) : (
                                                    <ArrowDropDownIcon></ArrowDropDownIcon>
                                                )}
                                            </Button>
                                        )}
                                        {/* 
                                          desktop & mobile menu - 
                                            desktop: displays instruments only
                                            mobile: displays instruments & other page links
                                         */}
                                        <Menu
                                            id="app-header-menu"
                                            MenuListProps={{ 'aria-labelledby': 'app-menu-selector' }}
                                            anchorEl={anchorEl}
                                            open={open}
                                            onClose={handleClose}
                                            PaperProps={{
                                                style: {
                                                    width: '13rem'
                                                }
                                            }}
                                        >
                                            {/* desktop & mobile */}
                                            <MenuItem
                                                data-testid="methanesat-dropdown-option"
                                                onClick={() => handlePlatformSelection(Platforms.MSAT)}
                                                disabled={selectedPlatform === Platforms.MSAT || ogiNearbyEnabled}
                                            >
                                                {t('platforms.MethaneSAT.long')}
                                            </MenuItem>
                                            {/* desktop & mobile */}
                                            <MenuItem
                                                data-testid="methaneair-dropdown-option"
                                                onClick={() => handlePlatformSelection(Platforms.MAIR)}
                                                disabled={selectedPlatform === Platforms.MAIR || ogiNearbyEnabled}
                                            >
                                                {t('platforms.MethaneAIR.long')}
                                            </MenuItem>
                                        </Menu>
                                    </Box>
                                </Grid>
                                <Grid>
                                    <Tooltip title={t('header.downloadLabel')} sx={{ marginLeft: 1, fontSize: 18 }}>
                                        <IconButton
                                            aria-label={t('header.downloadLabel')}
                                            data-testid="download-button"
                                            sx={{ paddingBottom: { xs: '4px', sm: '0px' }, color: 'inherit' }}
                                        >
                                            <Link
                                                data-testid="download-link"
                                                href={
                                                    'https://developers.google.com/earth-engine/datasets/publisher/edf-methanesat-ee'
                                                }
                                                target={'_blank'}
                                            >
                                                <DownloadIcon />
                                            </Link>
                                        </IconButton>
                                    </Tooltip>
                                    {isMainPage && (
                                        <IconButton
                                            data-testid="intro-modal-button"
                                            onClick={handleIntroModalOpen}
                                            sx={{
                                                paddingBottom: { xs: 1.5, sm: 1 }
                                            }}
                                            variant={introModalOpen === true ? 'active' : 'inactive'}
                                        >
                                            <InfoIcon />
                                        </IconButton>
                                    )}
                                    {isMainPage && (
                                        <Tooltip title={t('header.helpLabel')} sx={{ marginLeft: 1, fontSize: 18 }}>
                                            <IconButton
                                                ref={helpButtonRef}
                                                aria-label={t('header.helpLabel')}
                                                data-testid="help-button"
                                                sx={{
                                                    paddingBottom: { xs: '12px', sm: '8px' }
                                                }}
                                                onClick={onOpenLegend}
                                                variant={isLegendOpen ? 'active' : 'inactive'}
                                            >
                                                <HelpOutlineIcon id="header-help-button" />
                                            </IconButton>
                                        </Tooltip>
                                    )}
                                </Grid>
                                {/* mobile hamburger-button */}
                                {isMainPage && (
                                    <IconButton
                                        data-testid="app-menu-selector-mobile"
                                        aria-label={t('header.ariaMenuLabel')}
                                        id="app-menu-selector"
                                        aria-controls={open ? 'app-header-menu' : undefined}
                                        aria-expanded={open ? 'true' : undefined}
                                        aria-haspopup="true"
                                        onClick={handleClick}
                                        sx={{ display: { xs: 'block', sm: 'none' }, color: 'inherit' }}
                                    >
                                        <MenuIcon fontSize="large" />
                                    </IconButton>
                                )}
                            </Grid>
                        </Grid>
                    </Grid>
                </Toolbar>
                <IntroModal open={introModalOpen} action={handleIntroModalAction} />
            </AppBar>
        </>
    );
};

export default Header;
